/**
 * Bundle-driven product media
 *
 * When a Bundler quantity-break bundle renders with its own variant picker, it
 * hides the theme's picker by setting an inline `display: none` on
 * `.product-details variant-picker` (bundler-script.js, targetVariantPickerElementSelectors).
 * The bundle's dropdowns then become the only way to pick a variant -- but
 * nothing tells the main media gallery about those picks, so the hero image
 * stays on whatever variant the page happened to load with.
 *
 * Horizon's MediaGallery only swaps images on `variant:update`, and it does so
 * by replacing itself with the <media-gallery> out of a freshly fetched
 * document. Each variant's media set is rendered server-side, so the other
 * variants' images genuinely are not in the DOM -- fetching is the only way.
 *
 * So: watch Bundler's selects and swap the gallery to the chosen variant's
 * server-rendered media. Last pick wins, which is the behaviour you want for a
 * multi-item bundle where each row chooses its own variant.
 *
 * Deliberately scoped to the gallery: price and the cart form are left alone,
 * because in a 2-item bundle the "current variant" is ambiguous and Bundler
 * owns the add-to-cart payload.
 */
(function () {
  'use strict';

  var SCOPE_SELECTOR = '.bndlr-variant-picker-quantity-break, .bndlr-product, .bndlr-volume';
  var HIDDEN_SELECT = 'select.bndlr-select-variant[name="variant_id"]';
  var OPTION_SELECTS = 'select.bndlr-select-option[name^="option"], select.bndlr-option-select[name^="option"]';

  /** @type {Map<string, Document>} */
  var docCache = new Map();
  /** @type {Set<string>|null} */
  var ownVariantIds = null;
  var requestToken = 0;

  function productPath() {
    var host =
      document.querySelector('product-form-component[data-product-url]') ||
      document.querySelector('variant-picker[data-product-url]');

    var url = host && host.dataset.productUrl;
    if (!url) url = window.location.pathname;

    return String(url).split('?')[0].split('#')[0];
  }

  /** Variant ids belonging to the product on this page, so bundle rows for other products are ignored. */
  function loadOwnVariantIds() {
    if (ownVariantIds) return Promise.resolve(ownVariantIds);

    return fetch(productPath() + '.js')
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (product) {
        ownVariantIds = new Set(
          product && product.variants ? product.variants.map(function (v) { return String(v.id); }) : []
        );
        return ownVariantIds;
      })
      .catch(function () {
        ownVariantIds = new Set();
        return ownVariantIds;
      });
  }

  /**
   * Work out which variant a bundle row is pointing at.
   * Read from the option dropdowns rather than Bundler's hidden select, so we
   * don't race Bundler's own change handler.
   */
  function resolveVariantId(scope) {
    var hidden = scope.querySelector(HIDDEN_SELECT);
    if (!hidden) return null;

    var optionSelects = Array.prototype.slice
      .call(scope.querySelectorAll(OPTION_SELECTS))
      .sort(function (a, b) {
        return parseInt(a.name.replace('option', ''), 10) - parseInt(b.name.replace('option', ''), 10);
      });

    if (!optionSelects.length) return hidden.value || null;

    var values = optionSelects.map(function (select) { return select.value; });

    var match = Array.prototype.filter.call(hidden.options, function (option) {
      return values.every(function (value, index) {
        return option.getAttribute('data-option' + index) === value;
      });
    })[0];

    return match ? match.value : hidden.value || null;
  }

  /** Section id of the section holding the gallery, so we can fetch it alone. */
  function sectionId() {
    var gallery = document.querySelector('media-gallery');
    var section = gallery && gallery.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  function fetchVariantDocument(variantId) {
    if (docCache.has(variantId)) return Promise.resolve(docCache.get(variantId));

    // Section rendering keeps this to ~99KB instead of the ~383KB full page.
    var section = sectionId();
    var url = productPath() + '?variant=' + encodeURIComponent(variantId);
    if (section) url += '&section_id=' + encodeURIComponent(section);

    return fetch(url)
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        docCache.set(variantId, doc);
        return doc;
      })
      .catch(function () {
        return null;
      });
  }

  function showVariantMedia(variantId) {
    var token = ++requestToken;

    loadOwnVariantIds()
      .then(function (ids) {
        if (ids.size && !ids.has(String(variantId))) return null;
        return fetchVariantDocument(variantId);
      })
      .then(function (doc) {
        // A newer pick landed while this was in flight.
        if (!doc || token !== requestToken) return;

        var fresh = doc.querySelector('media-gallery');
        var current = document.querySelector('media-gallery');
        if (!fresh || !current) return;

        // Clone so the cached document keeps its own copy for the next pick.
        current.replaceWith(fresh.cloneNode(true));
      });
  }

  /**
   * Warm the CDN before the customer picks.
   *
   * Shopify generates each resized image on first request: a cold URL can take
   * seconds, the same URL afterwards ~65ms. Desktop picks wider, rarer widths
   * than mobile, so it hits cold URLs far more often -- which is the whole
   * reason the swap felt slow on desktop and fine on phones.
   *
   * Setting srcset + sizes on a detached Image makes the browser pick and fetch
   * exactly the candidate it would pick for real, so the later swap is a cache
   * hit. Runs one variant at a time: warming is background work and should
   * never compete with the page for bandwidth.
   */
  function warmImage(doc) {
    var img = doc.querySelector('media-gallery img');
    if (!img) return;

    var warm = new Image();
    var sizes = img.getAttribute('sizes');
    var srcset = img.getAttribute('srcset');

    if (sizes) warm.sizes = sizes;
    if (srcset) warm.srcset = srcset;
    else if (img.getAttribute('src')) warm.src = img.getAttribute('src');
  }

  var warmed = false;

  function warmAll() {
    if (warmed) return;
    warmed = true;

    loadOwnVariantIds().then(function (ids) {
      Array.from(ids).reduce(function (chain, id) {
        return chain.then(function () {
          return fetchVariantDocument(id).then(warmImage);
        });
      }, Promise.resolve());
    });
  }

  function onIntent(event) {
    if (event.target.closest && event.target.closest(SCOPE_SELECTOR)) warmAll();
  }

  function onChange(event) {
    var select = event.target;
    if (!select || select.tagName !== 'SELECT') return;
    if (!select.matches(HIDDEN_SELECT) && !select.matches(OPTION_SELECTS)) return;

    var scope = select.closest(SCOPE_SELECTOR);
    if (!scope) return;

    var variantId = select.matches(HIDDEN_SELECT) ? select.value : resolveVariantId(scope);
    if (variantId) showVariantMedia(variantId);
  }

  document.addEventListener('change', onChange);
  document.addEventListener('pointerenter', onIntent, true);
  document.addEventListener('focusin', onIntent);
})();
